import * as THREE from 'three'
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import type { FaixaLongitudinal, PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { formaLateralCarroceria, OPCOES_EXTRUSAO } from './formasCarroceria'
import { criarCaixaArredondada, densificarForma, geometriaBase } from './malhaDeformavel'

/**
 * Geometria da lateral deformável.
 *
 * A intrusão da porta vem do campo (`deslocamentoLateral` em
 * `campoDeformacao.ts`): região central entrando, soleira e teto resistindo,
 * barra de proteção como nervura mais dura, vincos diagonais e a "prateleira"
 * horizontal na altura do para-choque de quem bate. Aqui ficam as bases
 * subdivididas em que esse campo tem vértices para trabalhar.
 */

/** Chapa externa da porta: fina em X, subdividida na altura e no comprimento. */
export function baseChapaPorta(alturaChapa: number, comprimento: number): THREE.BufferGeometry {
  return geometriaBase(`chapaPorta:${alturaChapa.toFixed(3)}:${comprimento.toFixed(3)}`, () =>
    criarCaixaArredondada(0.045, alturaChapa, comprimento, [1, 12, 16], 0.01),
  )
}

/** Porta fechada do veículo-alvo (sem interior modelado). */
export function basePortaSolida(perfil: PerfilVeiculo, faixa: FaixaLongitudinal, espessura: number): THREE.BufferGeometry {
  const altura = perfil.cinturaY - perfil.topoSoleiraY - 0.02
  const comprimento = faixa.zDianteiro - faixa.zTraseiro - 0.07
  return geometriaBase(`portaSolida:${perfil.id}:${comprimento.toFixed(3)}`, () =>
    criarCaixaArredondada(espessura, altura, comprimento, [1, 12, 16], 0.008),
  )
}

/** Recorta um anel pelo semiplano `lado(p) >= 0` (um passo de Sutherland–Hodgman). */
function recortar(anel: THREE.Vector2[], eixo: 'x' | 'y', limite: number, manterMaior: boolean): THREE.Vector2[] {
  const saida: THREE.Vector2[] = []
  const dentroDe = (p: THREE.Vector2) => (manterMaior ? p[eixo] >= limite : p[eixo] <= limite)
  for (let i = 0; i < anel.length; i += 1) {
    const a = anel[i]!
    const b = anel[(i + 1) % anel.length]!
    const aDentro = dentroDe(a)
    const bDentro = dentroDe(b)
    if (aDentro) saida.push(a)
    if (aDentro !== bDentro) {
      const t = (limite - a[eixo]) / (b[eixo] - a[eixo])
      saida.push(new THREE.Vector2(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t))
    }
  }
  return saida
}

const recortarNaCelula = (anel: THREE.Vector2[], x0: number, x1: number, y0: number, y1: number) =>
  recortar(recortar(recortar(recortar(anel, 'x', x0, true), 'x', x1, false), 'y', y0, true), 'y', y1, false)

/**
 * Triangulação da face da chapa lateral em grade regular.
 *
 * O earcut sobre o contorno produz um leque de triângulos longos, e deformados
 * eles aparecem como facetas e listras. Aqui a face é dividida em células
 * quadradas: as internas viram dois triângulos; as de borda são recortadas
 * pela silhueta e trianguladas localmente. Triângulos pequenos e bem
 * formados em toda a chapa — a dobra fica lisa.
 */
export function triangularFaceLateral(forma: THREE.Shape, passo = 0.06): { faces: number[][]; todos: THREE.Vector2[] } {
  const pontos = forma.extractPoints(OPCOES_EXTRUSAO.curveSegments)
  const contorno = pontos.shape
  const furos = pontos.holes
  const caixa = new THREE.Box2().setFromPoints(contorno)
  const todos: THREE.Vector2[] = []
  const faces: number[][] = []

  for (let x0 = caixa.min.x; x0 < caixa.max.x - 1e-9; x0 += passo) {
    for (let y0 = caixa.min.y; y0 < caixa.max.y - 1e-9; y0 += passo) {
      const x1 = Math.min(x0 + passo, caixa.max.x)
      const y1 = Math.min(y0 + passo, caixa.max.y)
      const recorte = recortarNaCelula(contorno, x0, x1, y0, y1)
      if (recorte.length < 3 || Math.abs(THREE.ShapeUtils.area(recorte)) < 1e-9) continue
      const furosCelula = furos
        .map((furo) => recortarNaCelula(furo, x0, x1, y0, y1))
        .filter((furo) => furo.length >= 3 && Math.abs(THREE.ShapeUtils.area(furo)) > 1e-9)
      // sentido anti-horário para o contorno e horário para os furos, como a
      // ExtrudeGeometry espera depois de inverter
      const anel = THREE.ShapeUtils.isClockWise(recorte) ? recorte.slice().reverse() : recorte.slice()
      const aneisFuros = furosCelula.map((f) => (THREE.ShapeUtils.isClockWise(f) ? f.slice() : f.slice().reverse()))
      const locais = THREE.ShapeUtils.triangulateShape(anel, aneisFuros)
      const base = todos.length
      todos.push(...anel, ...aneisFuros.flat())
      for (const [a, b, c] of locais) faces.push([base + a!, base + b!, base + c!])
    }
  }
  return { faces, todos }
}

/**
 * Extrusão lateral pronta para deformar.
 *
 * Mesma silhueta e mesmo chanfro da versão intacta (`geometriaLateralDoPerfil`
 * na Carroceria), mas com as duas faces retrianguladas com pontos internos e
 * vértices fundidos (normais suaves). Só serve de base para o clone deformado.
 */
export function baseLateralDensa(perfil: PerfilVeiculo, espessura: number): THREE.BufferGeometry {
  return geometriaBase(`lateralDensa:${perfil.id}`, () => {
    const forma = densificarForma(formaLateralCarroceria(perfil), 0.07, OPCOES_EXTRUSAO.curveSegments)
    const extrusao = new THREE.ExtrudeGeometry(forma, { ...OPCOES_EXTRUSAO, depth: espessura })
    const posicoesExtrusao = extrusao.getAttribute('position') as THREE.BufferAttribute
    const lados = extrusao.groups.find((grupo) => grupo.materialIndex === 1)

    const { faces, todos } = triangularFaceLateral(forma)

    const bt = OPCOES_EXTRUSAO.bevelThickness
    const saida: number[] = []
    // triângulos anti-horários: a tampa de cima (+Z) na ordem, a de baixo invertida
    for (const [a, b, c] of faces) {
      for (const i of [c, b, a]) saida.push(todos[i!]!.x, todos[i!]!.y, -bt)
    }
    for (const [a, b, c] of faces) {
      for (const i of [a, b, c]) saida.push(todos[i!]!.x, todos[i!]!.y, espessura + bt)
    }
    if (lados) {
      for (let i = lados.start; i < lados.start + lados.count; i += 1) {
        saida.push(posicoesExtrusao.getX(i), posicoesExtrusao.getY(i), posicoesExtrusao.getZ(i))
      }
    }
    extrusao.dispose()
    const geometria = new THREE.BufferGeometry()
    geometria.setAttribute('position', new THREE.Float32BufferAttribute(saida, 3))
    const fundida = mergeVertices(geometria, 1e-5)
    fundida.computeVertexNormals()
    return fundida
  })
}
